import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { ModalBodyWrapper, ModalWrapper, SmallContentContainer, useEnterpriseBuildLocation } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { TextKey, ActionType, RouteName } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { CommanderThunkDispatch, WithAvailabilityBlockId, WithGuideLink } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { deleteAvailabilityBlockHeadingAction } from '../actions';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { useAvailabilityBlockInfo } from '../../availabilityBlock/utils/useAvailabilityBlockInfo';
import { changeDocumentTitle, changeDocumentTitleLocalized } from '../../../middleware';
import { AvailabilityBlockFormCreateFormModal } from './AvailabilityBlockFormCreateFormModal';
import { AvailabilityBlockFormEditFormModal } from './AvailabilityBlockFormEditFormModal';

interface AvailabilityBlockFormContainerProps {
    actions: HeadingAction[];
    breadcrumbs: Breadcrumbs;
    children: React.ReactNode;
}

interface AvailabilityBlockFormProps {
    form: string;
    serviceId: string;
}

const AvailabilityBlockFormContainer: React.FC<AvailabilityBlockFormContainerProps> = ({
    actions,
    breadcrumbs,
    children,
}) => (
    <ModalWrapper>
        <ModalHeader actions={actions} breadcrumbs={breadcrumbs} />
        <ModalBodyWrapper padded>
            <SmallContentContainer condensed>{children}</SmallContentContainer>
        </ModalBodyWrapper>
    </ModalWrapper>
);

export const AvailabilityBlockCreateForm: React.FC<AvailabilityBlockFormProps & WithGuideLink> = ({
    form,
    serviceId,
    guideLink,
}) => {
    const buildLocation = useEnterpriseBuildLocation();
    const availabilityBlockReportUrl = buildLocation({ to: 'AvailabilityBlockReport/Index' }).href;

    const actions: HeadingAction[] = [
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ];

    const breadcrumbs: Breadcrumbs = [
        { text: <LocalizedText textKey={TextKey.AvailabilityBlockPlural} />, url: availabilityBlockReportUrl },
        { text: <LocalizedText textKey={TextKey.AvailabilityBlock} />, active: true },
    ];

    return (
        <AvailabilityBlockFormContainer actions={actions} breadcrumbs={breadcrumbs}>
            <AvailabilityBlockFormCreateFormModal form={form} serviceId={serviceId} />
        </AvailabilityBlockFormContainer>
    );
};

export const AvailabilityBlockEditForm: React.FC<AvailabilityBlockFormProps & WithAvailabilityBlockId> = ({
    availabilityBlockId,
    form,
    serviceId,
}) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const resolveRoute = useResolveRoute();
    const { availabilityBlock, url: availabilityBlockUrl } = useAvailabilityBlockInfo(availabilityBlockId);

    const availabilityBlockName = availabilityBlock?.name ?? '';

    useEffect(() => {
        if (availabilityBlockName) {
            dispatch(changeDocumentTitle(availabilityBlockName));
        } else {
            dispatch(changeDocumentTitleLocalized(TextKey.AvailabilityBlock));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availabilityBlockName]);

    const availabilityBlockReportUrl = resolveRoute(RouteName.AvailabilityBlockReportIndex);

    const actions: HeadingAction[] = [
        {
            icon: IconName.Delete,
            actionId: 'delete',
            text: <LocalizedText textKey={TextKey.DeleteHint} />,
            title: (
                <LocalizedText textKey={TextKey.DeleteConfirmation} substitutes={{ entity: availabilityBlockName }} />
            ),
            buttonText: <LocalizedText textKey={TextKey.Delete} />,
            isDanger: true,
            onConfirmation: () =>
                dispatch(deleteAvailabilityBlockHeadingAction({ availabilityBlockId, availabilityBlockName })),
            type: ActionType.ConfirmationDialog,
        },
    ];

    const breadcrumbs: Breadcrumbs = [
        { text: <LocalizedText textKey={TextKey.AvailabilityBlockPlural} />, url: availabilityBlockReportUrl },
        { text: <LocalizedText textKey={TextKey.Dashboard} />, url: availabilityBlockUrl ?? '' },
        { text: availabilityBlockName, active: true },
    ];

    return (
        <AvailabilityBlockFormContainer actions={actions} breadcrumbs={breadcrumbs}>
            <AvailabilityBlockFormEditFormModal
                availabilityBlockId={availabilityBlockId}
                form={form}
                serviceId={serviceId}
            />
        </AvailabilityBlockFormContainer>
    );
};
